import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';

import { useAppResume } from '@/hooks/useAppResume';
import { markInboxSupportThreadRead } from '@/services/conversations/cache';
import { conversationKeys } from '@/services/conversations/keys';
import {
  lastStoredIndex,
  markSupportThreadRead,
  type SupportThread,
} from '@/services/conversations/thread';
import { invalidateUnreadInquiriesDebounced } from '@/util/inboxInvalidation';

const MARK_READ_DEBOUNCE_MS = 500;

function isAppInForeground(): boolean {
  if (Platform.OS === 'web') {
    return typeof document === 'undefined' || document.visibilityState === 'visible';
  }

  return AppState.currentState === 'active';
}

/**
 * Marks a support thread read up to the newest stored message while the
 * reader can actually see it: the view focused and the app in the
 * foreground. A message that arrives in the background stays unread until
 * the reader comes back, which is when this runs again.
 *
 * Only stored messages count. A pending send has no index yet, and the
 * reader's own messages never make a thread unread anyway.
 *
 * Posts are debounced so a burst of arrivals posts once, and skipped when the
 * server already has the index, since it never moves read state backwards.
 */
export function useMarkSupportThreadRead(
  conversationId: string,
  thread: SupportThread | undefined,
  { isFocused }: { isFocused: boolean }
): void {
  const queryClient = useQueryClient();

  const [resumeCount, setResumeCount] = useState(0);

  const postedIndexRef = useRef(-1);

  useAppResume(() => setResumeCount((count) => count + 1));

  const targetIndex = lastStoredIndex(thread);

  const serverReadIndex = thread?.meta.lastReadIndex ?? -1;

  useEffect(() => {
    if (!isFocused || targetIndex < 0) {
      return undefined;
    }

    if (targetIndex <= serverReadIndex || targetIndex <= postedIndexRef.current) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (!isAppInForeground()) {
        return;
      }

      postedIndexRef.current = targetIndex;

      markSupportThreadRead(conversationId, targetIndex)
        .then((lastReadIndex) => {
          queryClient.setQueryData<SupportThread>(
            conversationKeys.thread(conversationId),
            (current) =>
              current
                ? {
                    ...current,
                    meta: { ...current.meta, lastReadIndex: lastReadIndex ?? targetIndex },
                  }
                : current
          );

          markInboxSupportThreadRead(queryClient, conversationId);

          invalidateUnreadInquiriesDebounced(queryClient, 0);
        })
        .catch(() => {
          postedIndexRef.current = -1;
        });
    }, MARK_READ_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [conversationId, isFocused, queryClient, resumeCount, serverReadIndex, targetIndex]);
}
